// Converts a string to sentence case
// e.g. "this is a sentence. this is another sentence."
// becomes "This is a sentence. This is another sentence."

export function toSentenceCase(str: string) {
  return str.toLowerCase().replace(/(^\w{1}|\.\s*\w{1})/gi, (firstLetter) => {
    return firstLetter.toUpperCase();
  });
}

// Truncates a string to a certain length
export function truncateString(str: string, length: number) {
  if (str.length > length) {
    return str.slice(0, length) + "...";
  }
  return str;
}

// Common HTML entities map for fast decoding
const HTML_ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&apos;": "'",
  "&#39;": "'",
  "&nbsp;": " ",
  "&ndash;": "\u2013", // en dash
  "&mdash;": "\u2014", // em dash
  "&lsquo;": "\u2018", // left single quote
  "&rsquo;": "\u2019", // right single quote
  "&ldquo;": "\u201C", // left double quote
  "&rdquo;": "\u201D", // right double quote
  "&hellip;": "\u2026", // ellipsis
  "&copy;": "\u00A9",
  "&reg;": "\u00AE",
  "&trade;": "\u2122",
  "&bull;": "\u2022",
  "&pound;": "\u00A3",
  "&euro;": "\u20AC",
  "&yen;": "\u00A5",
  "&cent;": "\u00A2",
};

/**
 * Decodes HTML entities in a string efficiently.
 * Handles common named entities and numeric character references.
 */
export function decodeHtmlEntities(str: string): string {
  if (!str || !str.includes("&")) return str;

  // Replace named entities from map
  let result = str.replace(/&[a-zA-Z]+;/g, (match) => HTML_ENTITIES[match] ?? match);

  // Handle numeric character references (&#123; or &#x1F4A1;)
  result = result.replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)));
  result = result.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)));

  return result;
}

/** Filters out words that are not allowed in a tag recommendation query.
 * Rules:
 * 1. If the word starts with a '$', allow it.
 * 2. If the word is all uppercase, allow it.
 * 3. If the word is longer than 3 letters, allow it.
 */
export function filterTagRecommendQuery(text: string) {
  // Split text into words using whitespace
  const words = text.split(/\s+/);

  const allowed: string[] = [];

  words.forEach((word) => {
    // Remove punctuation for checking if needed
    const cleaned = word.replace(/[^\w\$]/g, "");

    // Allow words that start with $ (company tickers)
    if (cleaned.startsWith("$")) return allowed.push(cleaned);

    // Allow words that are in all uppercase (like "IBM")
    if (cleaned === cleaned.toUpperCase()) return allowed.push(cleaned);

    // Only allow if longer than 3 letters
    return cleaned.length > 3 && allowed.push(cleaned);
  });

  // join the filtered words
  return allowed.join(" ");
}

/**
 * Creates a regex for word boundary matching.
 * Uses negative lookbehind/lookahead to handle special chars like parentheses and commas.
 * Standard \b word boundaries don't work well with special characters.
 *
 * @param terms - Array of terms to match
 * @param caseInsensitive - Whether to match case-insensitively (default: true)
 * @returns RegExp that matches the terms with word boundaries
 *
 * @example
 * // Case-sensitive ticker matching (won't match "on" in "online")
 * const tickerRegex = createWordBoundaryRegex(["AAPL", "ON"], false);
 *
 * @example
 * // Case-insensitive keyword matching
 * const keywordRegex = createWordBoundaryRegex(["trump", "biden"], true);
 */
export function createWordBoundaryRegex(
  terms: string[],
  caseInsensitive: boolean = true
): RegExp {
  const escapedTerms = terms.map((term) =>
    term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  const flags = caseInsensitive ? "i" : "";
  return new RegExp(
    `(?<![a-zA-Z0-9_])(${escapedTerms.join("|")})(?![a-zA-Z0-9_])`,
    flags
  );
}
